import { Injectable } from "@angular/core";
import { DeviceService } from "@mydocma/core/src/lib/device";
import { isIosDevice } from "./shared/keyboard-viewport.util";
import { RpKeyboardScrollLockService } from "./ios/keyboard-scroll-lock.service";
import { RpKeyboardFocusGuardService } from "./ios/keyboard-focus-guard.service";
import { RpKeyboardPanHealService } from "./ios/keyboard-pan-heal.service";
import { RpKeyboardStateService } from "./keyboard-state.service";
import { RpKeyboardScrollerFreezeService } from "./keyboard-scroller-freeze.service";
import { RpKeyboardFocusRevealService } from "./keyboard-focus-reveal.service";
import { RpKeyboardScrollRoomService } from "./keyboard-scroll-room.service";
import { RpKeyboardOverlayAnchorService } from "./keyboard-overlay-anchor.service";

/**
 * On-screen keyboard handling, ported from mm-app (mm-common/mobile-keyboard, MmMobileKeyboardService).
 * The services that mm starts for its own tablet surfaces (fault dialog fit, scroll handoff, dropdown
 * guard, modal freeze) are left out.
 */
@Injectable({ providedIn: 'root' })
export class RpMobileKeyboardService {

    constructor(
        private readonly deviceService: DeviceService,
        private readonly iosScrollLock: RpKeyboardScrollLockService,
        private readonly iosFocusGuard: RpKeyboardFocusGuardService,
        private readonly keyboardState: RpKeyboardStateService,
        private readonly scrollerFreeze: RpKeyboardScrollerFreezeService,
        private readonly focusReveal: RpKeyboardFocusRevealService,
        private readonly iosPanHeal: RpKeyboardPanHealService,
        private readonly scrollRoom: RpKeyboardScrollRoomService,
        private readonly overlayAnchor: RpKeyboardOverlayAnchorService,
    ) {
    }

    get isEnabled(): boolean {
        // no soft keyboard to compensate for: visualViewport still shrinks on desktop pinch-zoom, which
        // would set rp-keyboard-open and swap every footer. DeviceService already counts iPadOS as tablet.
        return !this.deviceService.isDesktop();
    }

    init(): void {
        this.iosScrollLock.init();
        if (!this.isEnabled) {
            return;
        }
        const isTablet = typeof document !== 'undefined' && this.deviceService.isTablet();
        if (isTablet) {
            document.documentElement.classList.add('rp-tablet-keyboard');
        }
        if (isIosDevice()) {
            // iOS-only: WebKit pans the document on focus (Android keyboards don't) — see the guard
            this.iosFocusGuard.start();
            this.iosPanHeal.start();
        }
        this.keyboardState.start();
        this.scrollerFreeze.start();
        this.focusReveal.start();
        this.overlayAnchor.start();
        this.scrollRoom.start();
    }
}
